/**
 * Other Services Content - Comprehensive travel services management module
 * Includes: Transfers, Insurance, Visa & Documentation, Excursions, Currency Exchange
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useLocalization } from '../../../hooks/useLocalization';
import { useAuth } from '../../../contexts/AuthContext';
import HtmlEditor from '../HtmlEditor';

// Types
interface AdditionalService {
    _id: string;
    name: string;
    description?: string;
    category: 'transfer' | 'insurance' | 'visa' | 'excursion' | 'currency' | 'other';
    destinationId?: { _id: string; name: string; country: string } | null;
    pricing: {
        amount: number;
        currency: string;
        type: 'per_person' | 'per_booking' | 'per_night' | 'per_item';
    };
    isActive: boolean;
    // Specific fields can be stored in mixed fields or separate schema, 
    // for simplicity we map them from the backend 'requirements' or specific fields if extended
    requirements?: any;
    destinations?: any[]; // Legacy support or multi-dest
}

interface Destination {
    _id: string;
    name: string;
    country: string;
}

const API_BASE = '/api/admin/other-services';
const DEST_API = '/api/destinations';

const OtherServicesContent: React.FC<{ theme?: 'light' | 'dark' }> = ({ theme = 'light' }) => {
    const { t: translate, language } = useLocalization(); // Rename to avoid conflict if needed, or just use t
    const t = (key: string) => key; // simplified for now or use actual translation key map
    const { token } = useAuth();

    // State
    const [activeSection, setActiveSection] = useState<'transfer' | 'insurance' | 'visa' | 'excursion' | 'currency'>('transfer');
    const [services, setServices] = useState<AdditionalService[]>([]);
    const [destinations, setDestinations] = useState<Destination[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingService, setEditingService] = useState<AdditionalService | null>(null);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        currency: 'EUR',
        destinationId: '',
        isActive: true
    });

    const isDark = theme === 'dark';

    // Fetch Services
    const fetchServices = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}?category=${activeSection}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setServices(data.data);
            }
        } catch (err) {
            console.error('Failed to fetch services', err);
        }
        setLoading(false);
    };

    // Fetch Destinations
    const fetchDestinations = async () => {
        try {
            const res = await fetch(DEST_API);
            const data = await res.json();
            if (data.success) {
                setDestinations(data.data);
            }
        } catch (err) {
            console.error('Failed to fetch destinations', err);
        }
    };

    useEffect(() => {
        fetchServices();
    }, [activeSection]);

    useEffect(() => {
        fetchDestinations();
    }, []);

    // Handlers
    const handleOpenModal = (service: AdditionalService | null = null) => {
        setEditingService(service);
        if (service) {
            setFormData({
                name: service.name,
                description: service.description || '',
                price: service.pricing.amount.toString(),
                currency: service.pricing.currency,
                destinationId: service.destinationId?._id || '',
                isActive: service.isActive
            });
        } else {
            setFormData({
                name: '',
                description: '',
                price: '0',
                currency: 'EUR',
                destinationId: '',
                isActive: true
            });
        }

        console.log('Opening modal for service:', service ? service.name : 'New Service');
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        const payload = {
            name: formData.name,
            description: formData.description,
            category: activeSection,
            destinationId: formData.destinationId || null,
            pricing: {
                amount: parseFloat(formData.price),
                currency: formData.currency,
                type: 'per_person' // Default for now
            },
            isActive: formData.isActive
        };

        try {
            const url = editingService ? `${API_BASE}/${editingService._id}` : API_BASE;
            const method = editingService ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json();
            if (data.success) {
                setIsModalOpen(false);
                fetchServices();
            } else {
                alert('Error: ' + data.error);
            }
        } catch (err) {
            alert('Save failed');
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure?')) return;
        try {
            await fetch(`${API_BASE}/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchServices();
        } catch (err) {
            console.error('Delete failed', err);
        }
    };

    const sections = [
        { id: 'transfer', icon: 'local_taxi', label: 'Transfers' },
        { id: 'insurance', icon: 'health_and_safety', label: 'Insurance' },
        { id: 'visa', icon: 'badge', label: 'Visa' },
        { id: 'excursion', icon: 'hiking', label: 'Excursions' },
        { id: 'currency', icon: 'currency_exchange', label: 'Currency' },
    ];

    return (
        <div className={`flex-1 overflow-y-auto ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {/* Header */}
            <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Other Services</h1>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Manage additional travel services</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mt-6 overflow-x-auto">
                    {sections.map(s => (
                        <button
                            key={s.id}
                            onClick={() => setActiveSection(s.id as any)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeSection === s.id
                                ? 'bg-emerald-600 text-white'
                                : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                }`}
                        >
                            <span className="material-icons-round text-lg">{s.icon}</span>
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* List Content */}
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        {sections.find(s => s.id === activeSection)?.label} List
                    </h2>
                    <button
                        onClick={() => handleOpenModal()}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                        <span className="material-icons-round">add</span> Add New
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-10">Loading...</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {services.map(service => (
                            <div key={service._id} className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} hover:shadow-lg transition-shadow`}>
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{service.name}</h3>
                                    <span className={`px-2 py-0.5 rounded text-xs ${service.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                        {service.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-500 mb-2">{service.description}</p>
                                {service.destinationId && (
                                    <div className="text-xs text-blue-500 bg-blue-50 dark:bg-blue-900/20 inline-block px-2 py-1 rounded mb-2">
                                        📍 {service.destinationId.name}, {service.destinationId.country}
                                    </div>
                                )}
                                <div className="flex justify-between items-center mt-4">
                                    <span className="text-lg font-bold text-emerald-600">
                                        {service.pricing.amount} {service.pricing.currency}
                                    </span>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleOpenModal(service)} className="p-1 text-gray-400 hover:text-blue-500">
                                            <span className="material-icons-round">edit</span>
                                        </button>
                                        <button onClick={() => handleDelete(service._id)} className="p-1 text-gray-400 hover:text-red-500">
                                            <span className="material-icons-round">delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {services.length === 0 && (
                            <div className="col-span-full text-center py-10 text-gray-500">
                                No services found. Click "Add New" to create one.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal - Portal to Body */}
            {isModalOpen && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={(e) => {
                    if (e.target === e.currentTarget) setIsModalOpen(false);
                }}>
                    <div className={`w-full max-w-lg rounded-2xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-2xl relative animate-fadeIn`}>
                        <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {editingService ? 'Edit Service' : 'New Service'}
                        </h3>

                        <div className="space-y-4 max-h-[80vh] overflow-y-auto pr-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Name</label>
                                <input
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    className={`w-full p-2 rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
                                <HtmlEditor
                                    value={formData.description}
                                    onChange={html => setFormData({ ...formData, description: html })}
                                    isDark={isDark}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Price</label>
                                    <input
                                        type="number"
                                        value={formData.price}
                                        onChange={e => setFormData({ ...formData, price: e.target.value })}
                                        className={`w-full p-2 rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-1">Currency</label>
                                    <select
                                        value={formData.currency}
                                        onChange={e => setFormData({ ...formData, currency: e.target.value })}
                                        className={`w-full p-2 rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                    >
                                        <option value="EUR">EUR</option>
                                        <option value="USD">USD</option>
                                        <option value="SEK">SEK</option>
                                        <option value="TRY">TRY</option>
                                    </select>
                                </div>
                            </div>

                            {/* Destination Selector */}
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Destination (Optional)</label>
                                <select
                                    value={formData.destinationId}
                                    onChange={e => setFormData({ ...formData, destinationId: e.target.value })}
                                    className={`w-full p-2 rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                >
                                    <option value="">-- No Specific Destination --</option>
                                    {destinations.map(d => (
                                        <option key={d._id} value={d._id}>{d.name} ({d.country})</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.isActive}
                                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                                    id="isActive"
                                />
                                <label htmlFor="isActive" className={isDark ? 'text-white' : 'text-gray-900'}>Active</label>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default OtherServicesContent;
